import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Check, Star, AlertTriangle } from 'lucide-react';
import { ordersAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const STATUS_KEYS = {
  pending_payment: 'ord.status_pending_payment', paid: 'ord.status_paid', shipped: 'ord.status_shipped', delivered: 'ord.status_delivered',
};
const STATUS_COLORS = {
  pending_payment: 'bg-yellow-100 text-yellow-700', paid: 'bg-green-100 text-green-700', shipped: 'bg-blue-100 text-blue-700', delivered: 'bg-emerald-100 text-emerald-700',
};

export default function MyOrdersTab({ orders, setOrders }) {
  const { t } = useTranslation();
  const [ratingForm, setRatingForm] = useState({ orderId: null, rating: 5, comment: '' });

  const handleConfirmDelivery = async (orderId) => {
    try {
      await ordersAPI.confirmDelivery(orderId);
      toast.success(t('ord.delivery_confirmed'));
      const o = await ordersAPI.getMyOrders();
      setOrders(o.data);
    } catch (err) { toast.error(err.response?.data?.detail || t('ord.error')); }
  };

  const handleRate = async () => {
    try {
      await ordersAPI.rate(ratingForm.orderId, { rating: ratingForm.rating, comment: ratingForm.comment });
      toast.success(t('ord.thanks_review'));
      setRatingForm({ orderId: null, rating: 5, comment: '' });
      const o = await ordersAPI.getMyOrders();
      setOrders(o.data);
    } catch (err) { toast.error(err.response?.data?.detail || t('ord.error')); }
  };

  const handleReportDelivery = async (orderId) => {
    const reason = window.prompt(t('ord.report_prompt'));
    if (!reason) return;
    try {
      await ordersAPI.reportDeliveryIssue(orderId, reason);
      toast.success(t('ord.report_sent'));
      const o = await ordersAPI.getMyOrders();
      setOrders(o.data);
    } catch (err) { toast.error(err.response?.data?.detail || t('ord.error')); }
  };

  if (orders.length === 0) return (
    <div className="text-center py-16">
      <ShoppingBag className="w-12 h-12 text-[#B8D4E3] mx-auto mb-4" />
      <p className="text-[#5A8BA8] mb-4">{t('ord.no_orders')}</p>
      <Link to="/catalogue"><Button className="bg-[#32CD32] hover:bg-[#28a428]">{t('ord.see_listings')}</Button></Link>
    </div>
  );

  return (
    <div className="space-y-3">
      {orders.map(o => (
        <div key={o.id} className="bg-white rounded-xl border border-[#D6E8F0] p-3 sm:p-4" data-testid={`order-${o.id}`}>
          <div className="flex items-start sm:items-center justify-between gap-2 mb-2">
            <div className="min-w-0">
              <p className="font-semibold text-sm sm:text-base text-[#033871] truncate">{o.model}</p>
              <p className="text-xs text-[#5A8BA8]">#{o.id.slice(0, 8)}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="font-bold text-[#0C63BF]">{o.total.toFixed(2)} &euro;</p>
              <span className={`px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${STATUS_COLORS[o.status]}`}>
                {t(STATUS_KEYS[o.status], o.status)}
              </span>
            </div>
          </div>
          {o.tracking_number && (
            <p className="text-xs text-[#5A8BA8] mt-1">
              {t('ord.tracking')} <a href={o.tracking_url || `https://www.laposte.fr/outils/suivre-vos-envois?code=${encodeURIComponent(o.tracking_number)}`}
                target="_blank" rel="noopener noreferrer" className="font-mono text-[#0C63BF] hover:underline" data-testid={`tracking-link-buyer-${o.id}`}>{o.tracking_number}</a>
            </p>
          )}
          <div className="flex gap-2 mt-3">
            {o.status === 'shipped' && (
              <Button size="sm" onClick={() => handleConfirmDelivery(o.id)} className="bg-[#32CD32] hover:bg-[#28a428] gap-1" data-testid={`confirm-delivery-${o.id}`}>
                <Check className="w-3 h-3" /> {t('ord.confirm_reception')}
              </Button>
            )}
            {(o.status === 'delivered' || o.status === 'shipped') && !o.rated && (
              <Button size="sm" variant="outline" onClick={() => setRatingForm({ orderId: o.id, rating: 5, comment: '' })} data-testid={`rate-btn-${o.id}`}>
                <Star className="w-3 h-3 mr-1" /> {t('ord.rate_seller')}
              </Button>
            )}
            {(o.status === 'shipped' || o.status === 'delivered') && !o.delivery_issue && (
              <Button size="sm" variant="outline" onClick={() => handleReportDelivery(o.id)}
                className="gap-1 text-xs text-amber-600 border-amber-200 hover:bg-amber-50"
                data-testid={`report-delivery-${o.id}`}>
                <AlertTriangle className="w-3 h-3" /> {t('ord.report_issue')}
              </Button>
            )}
            {o.delivery_issue && (
              <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-lg" data-testid={`issue-badge-${o.id}`}>
                {t('ord.issue_reported')}
              </span>
            )}
          </div>
          {ratingForm.orderId === o.id && (
            <div className="mt-3 pt-3 border-t border-[#E8F4FD] space-y-3" data-testid={`rating-form-${o.id}`}>
              <p className="text-sm font-semibold text-[#033871]">{t('ord.rate_title')}</p>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map(s => (
                  <button key={s} onClick={() => setRatingForm(prev => ({ ...prev, rating: s }))} data-testid={`star-${s}`}>
                    <Star className={`w-7 h-7 transition-colors ${s <= ratingForm.rating ? 'fill-yellow-400 text-yellow-400' : 'text-[#D6E8F0] hover:text-yellow-300'}`} />
                  </button>
                ))}
                <span className="ml-2 text-sm font-bold text-[#033871] self-center">{ratingForm.rating}/5</span>
              </div>
              <textarea value={ratingForm.comment} onChange={e => setRatingForm(prev => ({ ...prev, comment: e.target.value }))}
                placeholder={t('ord.rate_placeholder')}
                className="w-full h-20 px-3 py-2 rounded-xl border border-[#D6E8F0] text-sm text-[#033871] placeholder-[#B8D4E3] resize-none focus:border-[#0C63BF] focus:ring-1 focus:ring-[#0C63BF] outline-none"
                data-testid="rating-comment-input" />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleRate} className="bg-[#32CD32] hover:bg-[#28a428]" data-testid="submit-rating">{t('ord.send')}</Button>
                <Button size="sm" variant="ghost" onClick={() => setRatingForm({ orderId: null, rating: 5, comment: '' })}>{t('ord.cancel')}</Button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
